import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import {
  Box,
  VStack,
  Input,
  Button,
  Text,
  Heading,
  useToast,
  FormControl,
  HStack,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useColorModeValue,
  Container,
  Grid,
  GridItem,
  Card,
  CardBody,
  Icon,
  Image,
  IconButton,
  Textarea,
} from "@chakra-ui/react";
import { FaPaperclip, FaFile, FaPaperPlane } from "react-icons/fa";
import { firestore, storage } from "../../firebase/firebaseConfig";
import {
  collection,
  addDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
  updateDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../context/AuthProvider";
import { useParams, Link } from "react-router-dom";
import FeedbackForm from "../../components/FeedbackForm";
import { fetchTradeByListingId } from "../../services/tradeService";
import { fetchTransactionByListingId } from "../../services/transactionService";
import { useTranslation } from "react-i18next";


/**
 * ChatPage Component
 */
const ChatPage = () => {
  const { t, i18n } = useTranslation();

  // ✅ HOOKS IN CORRECT ORDER
  const { chatId } = useParams();
  const { user } = useAuth();
  const toast = useToast();

  // 🛠️ State Variables
  const messagesEndRef = useRef(null); // Ref to the end of the message list
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [tradeStatus, setTradeStatus] = useState("");
  const [disputeReason, setDisputeReason] = useState("");
  const [isDisputeModalOpen, setIsDisputeModalOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [proofFile, setProofFile] = useState(null);
  const [userRole, setUserRole] = useState("");

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [tradeId, setTradeId] = useState("");
  const [listingId, setListingId] = useState("");
  const [tradeDetails, setTradeDetails] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [buyerId, setBuyerId] = useState("");
  const [sellerId, setSellerId] = useState("");


  // 🖌️ Color Variables
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBgColor = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headingColor = useColorModeValue("gray.900", "white");

  // Handle Language Change
  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  // 📡 Fetch Messages on Mount
  useEffect(() => {
    const messagesRef = collection(firestore, `chats/${chatId}/messages`);
    const messagesQuery = query(messagesRef, orderBy("timestamp", "asc"));

    const unsubscribeMessages = onSnapshot(messagesQuery, (snapshot) => {
      const fetchedMessages = snapshot.docs.map((doc) => doc.data());
      setMessages(fetchedMessages);
    });
    return () => unsubscribeMessages();
  }, [chatId]);

  // 🔍 Fetch Chat Details
  useEffect(() => {
    const fetchChatDetails = async () => {
      try {
        const chatDocRef = doc(firestore, "chats", chatId);
        const chatDoc = await getDoc(chatDocRef);

        if (!chatDoc.exists()) {
          throw new Error("Chat not found.");
        }

        const chatData = chatDoc.data();
        setUserRole(chatData.buyerId === user.uid ? "buyer" : "seller");

        setBuyerId(chatData.buyerId);
        setSellerId(chatData.sellerId);
        setListingId(chatData.listingId);

        // Fetch trade details securely
        const tradeDoc = await fetchTradeByListingId(chatData.listingId, user.uid);
        if (tradeDoc) {
          setTradeDetails(tradeDoc);
          setTradeId(tradeDoc.id);
          setTradeStatus(tradeDoc.status);



          /* // Fetch user profile
          const userRef = doc(firestore, "users", user.uid);
          // **Increment tradesThisCycle after a successful transaction**
          await updateDoc(userRef, { tradesThisCycle: tradesThisCycle + 1 });
   */
        } else {
          console.warn("No associated trade found.");
        }

        const transactionDoc = await fetchTransactionByListingId(chatData.listingId);
        setTransactionId(transactionDoc.id);

      } catch (error) {
        console.error("[fetchChatDetails] Error fetching chat:", error);
        toast({
          title: "Error",
          description: "Failed to load chat details. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    fetchChatDetails();
  }, [chatId, user.uid]);

  useEffect(() => {

    const fetchUploadedFiles = async () => {
      try {
        const chatDoc = await getDoc(doc(firestore, "chats", chatId));
        if (chatDoc.exists()) {
          const chatData = chatDoc.data();
          if (chatData.proofName) {
            setUploadedFiles([{ name: chatData.proofName }]);
          }
        }
      } catch (error) {
        console.error(t("chatpage.fetch_uploaded_files_error"), error);
      }
    };

    fetchUploadedFiles();

  }, [chatId]);

  useEffect(() => {
    // Scroll to the latest message whenever messages are updated
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const sendMessage = async (messageText, isSystemMessage = false, fileUrl = null) => {
    if (!user && !isSystemMessage) {
      toast({
        title: t("chatpage.auth_required_title"),
        description: t("chatpage.auth_required_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (messageText.trim() || fileUrl) {
      await addDoc(collection(firestore, `chats/${chatId}/messages`), {
        content: messageText,
        senderId: isSystemMessage ? "system" : user.uid,
        timestamp: serverTimestamp(),
        isSystemMessage,
        isRead: false,
        fileUrl: fileUrl,
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setProofFile(file);
    if (file) {
      uploadProof();
    }
  };

  const uploadProof = async () => {
    try {
      if (!proofFile) {
        toast({
          title: "No File Selected",
          description: "Please upload a file to proceed or skip if optional.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const fileRef = doc(firestore, `proofs/${chatId}/${proofFile.name}`);

      const uploadTask = uploadBytesResumable(fileRef, proofFile);

      await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log("Upload is " + progress + "% done");
            switch (snapshot.state) {
              case "paused":
                console.log("Upload is paused");
                break;
              case "running":
                console.log("Upload is running");
                break;
            }
          },
          (error) => {
            console.error("Upload Error:", error);
            toast({
              title: "Upload Error",
              description: error.message,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            reject(error);
          },
          async () => {
            try {
              const fileUrl = await getDownloadURL(uploadTask.snapshot.ref);
              const fileName = fileUrl.substring(fileUrl.lastIndexOf("/") + 1);

              const chatRef = doc(firestore, "chats", chatId);
              await updateDoc(chatRef, {
                proofName: fileName,
              });

              await sendMessage(`File uploaded by ${userRole}.`, true, fileUrl);

              toast({
                title: "Proof Uploaded",
                description: "The proof has been successfully uploaded.",
                status: "success",
                duration: 3000,
                isClosable: true,
              });

              setUploadedFiles([{ name: fileName }]);
              setProofFile(null);
              resolve();
            } catch (getDownloadURLError) {
              console.error("Error getting download URL:", getDownloadURLError);
              toast({
                title: "Download URL Error",
                description: getDownloadURLError.message,
                status: "error",
                duration: 5000,
                isClosable: true,
              });
              reject(getDownloadURLError
              );
            }
          }
        );
      });
    } catch (error) {
      console.error("Error uploading proof:", error);
      toast({
        title: "Error",
        description: "Failed to upload proof. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const updateStatus = async (newStatus, messageText = "") => {
    try {
      console.log(`Initiating status update. New Status: ${newStatus}, Message: ${messageText}`);
      if (!messageText.trim()) {
        messageText = `Trade status updated to "${newStatus}".`;
      }

      switch (newStatus) {
        case "awaiting payment":
          console.log("Updating status to awaiting payment");
          await updateChatStatus(chatId, "active");
          await updateTradeStatus(tradeId, newStatus);
          await updateTransactionStatus(transactionId, "pending");
          await updateListingStatus(listingId, "matched");
          setTradeStatus(newStatus);
          break;

        case "payment sent":
          console.log("Updating status to payment sent");
          await updateChatStatus(chatId, "active");
          await updateTradeStatus(tradeId, newStatus);
          await updateTransactionStatus(transactionId, "processing");
          await updateListingStatus(listingId, "matched");
          setTradeStatus(newStatus);
          break;

        case "currency delivered":
          console.log("Updating status to currency delivered");
          await updateChatStatus(chatId, "active");
          await updateTradeStatus(tradeId, newStatus);
          await updateTransactionStatus(transactionId, "processing");
          await updateListingStatus(listingId, "matched");
          setTradeStatus(newStatus);
          break;

        case "disputed":
          console.log("Updating status to disputed");
          await updateChatStatus(chatId, "escalated");
          await updateTradeStatus(tradeId, newStatus);
          await updateTransactionStatus(transactionId, "failed");
          await updateListingStatus(listingId, "expired");
          setTradeStatus(newStatus);
          break;

        case "canceled":
          console.log("Updating status to canceled");
          await updateChatStatus(chatId, newStatus);
          await updateTradeStatus(tradeId, newStatus);
          await updateTransactionStatus(transactionId, newStatus);
          await updateListingStatus(listingId, "archived");
          setTradeStatus(newStatus);
          break;

        case "completed":
          console.log("Updating status to completed");
          await updateChatStatus(chatId, "terminated");
          await updateTradeStatus(tradeId, newStatus);
          await updateTransactionStatus(transactionId, "successful");
          await updateListingStatus(listingId, "archived");
          setTradeStatus(newStatus);
          break;

        default:
          console.error(`Unhandled status: ${newStatus}`);
          throw new Error(`Unhandled status: ${newStatus}`);
      }

      console.log(`Sending status update message: ${messageText}`);
      await sendMessage(messageText, true);

      toast({
        title: `Status Updated: ${newStatus}`,
        description: "The trade status has been successfully updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: `Failed to update trade status: ${error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const updateChatStatus = async (chatId, status) => {
    try {
      const chatRef = doc(firestore, "chats", chatId);
      await updateDoc(chatRef, { status });
    } catch (error) {
      console.error("Error updating chat status:", error);
      throw error;
    }
  };

  const updateTradeStatus = async (tradeId, status) => {
    try {
      const tradeRef = doc(firestore, "trades", tradeId);
      await updateDoc(tradeRef, { status });
    } catch (error) {
      console.error("Error updating trade status:", error);
      throw error;
    }
  };

  const updateTransactionStatus = async (transactionId, status) => {
    try {
      if (!transactionId || typeof transactionId !== "string") {
        console.error("Invalid transaction ID provided:", transactionId);
        throw new Error("Invalid Transaction ID");
      }

      console.log(`Updating transaction status. ID: ${transactionId}, Status: ${status}`);
      const transactionRef = doc(firestore, "transactions", transactionId);
      console.log("Transaction doc reference created:", transactionRef.path);

      await updateDoc(transactionRef, { status });
      console.log(`Transaction status updated successfully: ${status}`);

    } catch (error) {
      console.error("Error updating transaction status:", error);
      throw error;
    }
  };

  const updateListingStatus = async (listingId, status) => {
    try {
      const listingRef = doc(firestore, "listings", listingId);
      await updateDoc(listingRef, { status });
    } catch (error) {
      console.error("Error updating listing status:", error);
      throw error;
    }
  };

  const cancelTrade = async () => {
    try {
      if (tradeStatus === "Payment Sent") {
        toast({
          title: "Cancellation Restricted",
          description: "Cancellation requires mutual agreement at this stage.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      updateStatus("canceled", `${userRole === "buyer" ? "Buyer" : "Seller"} requested trade cancellation.`);

      toast({
        title: "Trade Canceled",
        description: "The trade has been successfully canceled.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error canceling trade:", error);
      toast({
        title: "Error",
        description: "Failed to cancel the trade. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  /**
 * Raise a dispute and update the trade status
 */

  const raiseDispute = async (disputeReason) => {

    if (!disputeReason.trim()) {
      toast({
        title: "Dispute Reason Required",
        description: "Please enter a reason for the dispute.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const tradeDocRef = doc(firestore, "trades", tradeId);
      await updateDoc(tradeDocRef, {
        status: "disputed",
        disputeReason,
      });

      setIsDisputeModalOpen(false);
      updateStatus("disputed", `${userRole === "buyer" ? "Buyer" : "Seller"} has raised a dispute.`);
      setDisputeReason("");

      toast({
        title: "Dispute Raised",
        description: "The dispute has been raised. Please wait for platform mediation.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error raising dispute:", error);
      toast({
        title: "Error",
        description: "Failed to raise the dispute. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      bg="brand.300"
      color="brand.500"
      m="-80px"
      minHeight="150vh" // Adjusts height to allow room for the input field
    >
      <Container maxW="container.xl" p={20} >
        {tradeDetails && (
          <Card
            bg="brand.100"
            p={4}
            mb={4}
            borderRadius="md"
            shadow="md"
            borderLeft="4px solid teal"
            borderColor={borderColor}
          >
            <CardBody>
              <HStack justify="space-between" mb={4} alignItems="flex-start" flexWrap="wrap">
                <Heading size="md" color={headingColor}>
                  {t("chatpage.listing_details")}
                </Heading>
                <Badge colorScheme={tradeStatus === "disputed" ? "red" : "blue"}>{tradeStatus}</Badge>
              </HStack>
              <VStack spacing={2} align="start">
                <Text color={textColor}>
                  <strong>{t("chatpage.amount_label")}:</strong> {tradeDetails.amount} {tradeDetails.offeredCurrency}
                </Text>
                <Text color={textColor}>
                  <strong>{t("chatpage.exchange_rate_label")}:</strong> 1 {tradeDetails.offeredCurrency} = {tradeDetails.rate}{" "}
                  {tradeDetails.desiredCurrency}
                </Text>
                <Text color={textColor}>
                  <strong>{t("chatpage.payment_methods_label")}:</strong> {tradeDetails.paymentMethods.join(", ")}
                </Text>
                <Text color={textColor}>
                  <strong>{t("chatpage.location_label")}:</strong> {tradeDetails.location}
                </Text>
              </VStack>

              <VStack
                spacing={1}
                align="end"
                mt={{ base: 6, md: 0 }}
                position={{ base: "static", md: "absolute" }}
                bottom={{ base: "unset", md: 12 }}
                right={{ base: "unset", md: 12 }}
              >
                <Button
                  colorScheme="blue"
                  size="sm"
                  w={{ base: "100%", md: "auto" }}
                  onClick={() => setIsFeedbackModalOpen(true)}
                >
                  Rate Trade
                </Button>

                {(tradeStatus !== "payment sent" || tradeStatus !== "currency delivered") && (
                  <Link
                    color="orange.500"
                    fontSize="sm"
                    onClick={() => setIsDisputeModalOpen(true)}
                    __hover={{ textDecoration: "underline" }}
                    style={{ textAlign: "center" }}
                    mt={{ base: 2, md: 0 }}
                  >
                    Have an Issue? Raise a Dispute
                  </Link>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        <VStack spacing={8} align="stretch" borderRadius="2xl" bg="brand.100" ml="-25px" mr="-25px" mb="-96px" p="20px">
          <Heading fontSize="2xl" color={headingColor}>
            {t("chatpage.chat_messages")}
          </Heading>

          <VStack spacing={4}>
            {uploadedFiles.length > 0 && (
              <Box>
                <Text fontWeight="bold" color={headingColor}>
                  Uploaded Files:
                </Text>
                {uploadedFiles.map((file, index) => (
                  <Box key={index} p={2} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                    {file.name.toLowerCase().endsWith(".png") ||
                      file.name.toLowerCase().endsWith(".jpg") ||
                      file.name.toLowerCase().endsWith(".jpeg") ||
                      file.name.toLowerCase().endsWith(".gif") ||
                      file.name.toLowerCase().endsWith(".webp") ? (
                      <Image
                        src={`https://firebasestorage.googleapis.com/v0/b/pxtrade-dev.firebasestorage.app/o/proofs/<span class="math-inline">\{chatId\}/</span>{file.name}?alt=media`}
                        alt={file.name}
                        maxW="100px"
                        maxH="100px"
                        objectFit="cover"
                        borderRadius="md"
                        mb={2}
                        cursor="pointer"
                        onClick={() =>
                          window.open(
                            `https://firebasestorage.googleapis.com/v0/b/pxtrade-dev.firebasestorage.app/o/proofs/<span class="math-inline">\{chatId\}/</span>{file.name}?alt=media`,
                            "_blank"
                          )
                        }
                      />
                    ) : (
                      <a
                        href={`https://firebasestorage.googleapis.com/v0/b/pxtrade-dev.firebasestorage.app/o/proofs/<span class="math-inline">\{chatId\}/</span>{file.name}?alt=media`}
                        target="_blank"
                        rel="noopener noreferrer"
                        color={textColor}
                        style={{ display: "flex", alignItems: "center", gap: "4px" }}
                      >
                        <Icon as={FaFile} boxSize={4} color={textColor} />
                        <Text>{file.name}</Text>
                      </a>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </VStack>

          {/* Scrollable message container */}
          <Box flex="1" display="flex" flexDirection="column" p={2}>
            <Box
              flex="1"
              overflowY="auto"
              maxH={{ base: "600px", md: "600px" }} // Adjust max height for different screen sizes
              p={4}
              mt="-40px"
              bg="white"
              borderRadius="2xl"
              borderWidth="1px"
              borderColor={borderColor}
              scrollBehavior="smooth"
            >
              <Grid templateColumns="1fr" gap={4}>
                {messages.map((msg, index) => (
                  <GridItem
                    key={index}
                    display="flex"
                    justifyContent={msg.senderId === user.uid ? "flex-end" : "flex-start"}
                    width="100%"
                    p={{ base: 2, md: 0 }} // Add padding to GridItems for better mobile experience
                  >
                    <Box
                      bg={
                        msg.senderId === user.uid
                          ? useColorModeValue("brand.100", "blue.500")
                          : msg.isSystemMessage
                            ? useColorModeValue("brand.300", "gray.600")
                            : useColorModeValue("brand.200", "gray.500")
                      }
                      p={2}
                      borderRadius="lg"
                      boxShadow="sm"
                      maxW={{ base: "90%", sm: "80%", md: "60%" }} // Adjusted max width for mobile
                      w="fit-content" // Ensure the box takes only necessary width
                      color={
                        msg.senderId === user.uid
                          ? useColorModeValue("brand.500", "blue.500")
                          : msg.isSystemMessage
                            ? useColorModeValue("white", "gray.600")
                            : useColorModeValue("brand.400", "gray.500")
                      }
                      wordBreak="break-word" // Prevent long words from breaking the layout
                    >
                      {/*  <Text fontSize="sm" fontWeight="bold" mb={2} textAlign="left">
                        {msg.isSystemMessage ? "System Message" : msg.senderId === user.uid ? "You" : "Other User"}
                      </Text> */}

                      {msg.fileUrl ? (
                        <Box mt={2} p={2} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                          {(() => {
                            try {
                              const url = new URL(msg.fileUrl);
                              const pathname = url.pathname;
                              const ext = pathname.substring(pathname.lastIndexOf(".") + 1).toLowerCase();

                              if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) {
                                return (
                                  <Image
                                    src={msg.fileUrl}
                                    alt={msg.content}
                                    maxW="100%" // Make image responsive
                                    maxH="200px"
                                    objectFit="cover"
                                    borderRadius="md"
                                    mb={2}
                                    cursor="pointer"
                                    onClick={() => window.open(msg.fileUrl, "_blank")}
                                  />
                                );
                              } else {
                                return (
                                  <a
                                    href={msg.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: "flex", alignItems: "center", gap: "4px", color: textColor }}
                                  >
                                    <Icon as={FaFile} boxSize={6} />
                                    <Text fontSize="sm" noOfLines={1}>{msg.content.substring(msg.content.lastIndexOf("/") + 1)}</Text> {/* Added noOfLines prop */}
                                  </a>
                                );
                              }
                            } catch (error) {
                              console.error("Invalid URL:", error);
                              return null;
                            }
                          })()}
                        </Box>
                      ) : (
                        <Text mb={2}>{msg.content}</Text>
                      )}

                      <Text
                        fontWeight="light"
                        fontSize="2xs"
                        style={{ textAlign: "right" }}>
                        {msg.timestamp ? msg.timestamp.toDate().toLocaleString() : t("chatpage.unknown_time")}
                      </Text>
                    </Box>
                  </GridItem>
                ))}
              </Grid>
              <Box ref={messagesEndRef} />
            </Box>
          </Box>



          <FormControl display="flex" alignItems="center" gap={1}>
            <Input
              placeholder={t("chatpage.input_placeholder")}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              bg={cardBgColor}
              borderColor={borderColor}
              color={textColor}
              flex={1}
              borderRadius="3xl"
            />
            <IconButton
              icon={<FaPaperPlane />}
              aria-label={t("chatpage.send_message_button")}
              colorScheme="teal"
              size="md"
              onClick={async () => {
                try {
                  await sendMessage(newMessage);
                  setNewMessage("");

                  if (tradeStatus === "initiated" && userRole === "buyer") {
                    await updateStatus("awaiting payment", "The seller will reply shortly");
                  }
                } catch (error) {
                  console.error(t("chatpage.send_message_error"), error);
                  toast({
                    title: t("chatpage.send_message_error_title"),
                    description: t("chatpage.send_message_error_message"),
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                  });
                }
              }}
            />

            <IconButton
              icon={<FaPaperclip />}
              aria-label="Upload File"
              variant="ghost"
              colorScheme="blue"
              _hover={{ bg: "blue.50" }}
              size="md"
              onClick={() => document.getElementById("file-upload").click()}
            />

            <Input
              id="file-upload"
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFileChange}
              display="none"
            />
          </FormControl>

          <HStack spacing={4}>
            {((tradeStatus === "awaiting payment" || tradeStatus === "currency delivered") &&
              userRole === "seller") && (
                <Button
                  colorScheme="green"
                  onClick={() => {
                    updateStatus("payment sent", "Seller has sent the payment.");
                    setIsFeedbackModalOpen(true);
                  }}
                >
                  Payment Sent
                </Button>
              )}

            {((tradeStatus === "awaiting payment" || tradeStatus === "payment sent") &&
              userRole === "buyer") && (
                <Button
                  colorScheme="green"
                  onClick={() => {
                    updateStatus("currency delivered", "Buyer has confirmed the reception.");
                    setIsFeedbackModalOpen(true);
                  }}
                >
                  Confirm Completion
                </Button>
              )}

            {(tradeStatus !== "payment sent" || tradeStatus !== "currency delivered") && (
              <Button colorScheme="red" onClick={cancelTrade}>
                Cancel Trade
              </Button>
            )}
          </HStack>
        </VStack>

        <Modal isOpen={isFeedbackModalOpen} onClose={() => setIsFeedbackModalOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Don't forget to rate your trade!</ModalHeader>
            <ModalCloseButton
              isDisabled={tradeStatus !== "completed"}
              onClick={() => setIsFeedbackModalOpen(false)}
            />
            <ModalBody>
              <FeedbackForm
                tradeId={tradeId}
                chatId={chatId}
                role={userRole}
                buyerId={buyerId}
                sellerId={sellerId}
                onComplete={() => {
                  console.log("Feedback complete, closing modal.");
                  setIsFeedbackModalOpen(false);
                  updateStatus("completed");
                }}
              />
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Dispute Modal */}
        <Modal isOpen={isDisputeModalOpen} onClose={() => setIsDisputeModalOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Raise a Dispute</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={2}>Please describe the issue:</Text>
              <Textarea
                placeholder="Enter dispute reason..."
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
              />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="red" onClick={raiseDispute}>
                Submit Dispute
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

      </Container>
    </Box>
  );
};

export default ChatPage;